import { useId, useState } from "react";
import { useTranslations } from "@/lib/useTranslations";

import { FieldLabel } from "@/components/ui/FieldLabel";

import type {
  ActivityRecurrenceValue,
  ActivitySchedule,
  ActivitySchedulePonctuel,
  ActivityScheduleRecurrent,
  ActivityTypeValue,
} from "@/core/services/types/event.types";
import {
  ACTIVITY_RECURRENCE_VALUES,
  ACTIVITY_TYPE_VALUES,
  ACTIVITY_WEEKDAY_VALUES,
  normalizePonctuelSchedule,
  normalizeRecurrentSchedule,
} from "@/core/services/types/event.types";

const selectClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

const dateInputClassName =
  "wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

function formatActivityDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return date;

  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(parsed);
}

interface EventActivityFieldsProps {
  activityType: ActivityTypeValue | "";
  isFamily: boolean;
  isRecurrent: boolean;
  schedule: ActivitySchedule;
  disabled: boolean;
  fieldErrors?: Record<string, string>;
  onActivityTypeChange: (value: ActivityTypeValue | "") => void;
  onIsFamilyChange: (value: boolean) => void;
  onIsRecurrentChange: (value: boolean) => void;
  onScheduleChange: (schedule: ActivitySchedule) => void;
}

export function EventActivityFields({
  activityType,
  isFamily,
  isRecurrent,
  schedule,
  disabled,
  fieldErrors,
  onActivityTypeChange,
  onIsFamilyChange,
  onIsRecurrentChange,
  onScheduleChange,
}: EventActivityFieldsProps) {
  const t = useTranslations("events.edit.activity");
  const tActivityTypes = useTranslations("events.edit.activityTypes");
  const scheduleModeId = useId();
  const [newDate, setNewDate] = useState("");

  const ponctuelSchedule = isRecurrent
    ? null
    : (schedule as ActivitySchedulePonctuel);
  const recurrentSchedule = isRecurrent
    ? (schedule as ActivityScheduleRecurrent)
    : null;

  function handleAddDate() {
    if (!ponctuelSchedule || !newDate) return;
    if (ponctuelSchedule.dates.includes(newDate)) {
      setNewDate("");
      return;
    }

    onScheduleChange(
      normalizePonctuelSchedule([...ponctuelSchedule.dates, newDate]),
    );
    setNewDate("");
  }

  function handleRemoveDate(date: string) {
    if (!ponctuelSchedule) return;
    onScheduleChange(
      normalizePonctuelSchedule(
        ponctuelSchedule.dates.filter((entry) => entry !== date),
      ),
    );
  }

  function handleRecurrenceChange(value: ActivityRecurrenceValue) {
    if (!recurrentSchedule) return;
    onScheduleChange(
      normalizeRecurrentSchedule(value, recurrentSchedule.days),
    );
  }

  function handleDayToggle(day: number) {
    if (!recurrentSchedule) return;
    const nextDays = recurrentSchedule.days.includes(day)
      ? recurrentSchedule.days.filter((entry) => entry !== day)
      : [...recurrentSchedule.days, day];

    onScheduleChange(
      normalizeRecurrentSchedule(recurrentSchedule.reccurence, nextDays),
    );
  }

  return (
    <section
      className="space-y-6 wireframe-border bg-white p-5"
      aria-labelledby="event-activity-section-label"
    >
      <h2
        id="event-activity-section-label"
        className="text-base font-semibold text-whart-text"
      >
        {t("sectionLabel")}
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <FieldLabel htmlFor="event-activity-type" required>
            {t("typeLabel")}
          </FieldLabel>
          <select
            id="event-activity-type"
            name="activity_type"
            value={activityType}
            disabled={disabled}
            onChange={(event) =>
              onActivityTypeChange(event.target.value as ActivityTypeValue | "")
            }
            className={selectClassName}
          >
            <option value="">{t("typePlaceholder")}</option>
            {ACTIVITY_TYPE_VALUES.map((type) => (
              <option key={type} value={type}>
                {tActivityTypes(type)}
              </option>
            ))}
          </select>
          <FieldError message={fieldErrors?.activity_type} />
        </div>

        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm font-medium text-whart-text">
            <input
              type="checkbox"
              name="is_family"
              value="true"
              checked={isFamily}
              disabled={disabled}
              onChange={(event) => onIsFamilyChange(event.target.checked)}
              className="size-4 rounded border border-black/10"
            />
            {t("familyLabel")}
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <FieldLabel as="p" required>
          {t("scheduleLabel")}
        </FieldLabel>

        <input type="hidden" name="is_recurrent" value={String(isRecurrent)} />

        <div
          role="radiogroup"
          aria-labelledby={`${scheduleModeId}-label`}
          className="flex flex-wrap gap-1 rounded-xl wireframe-border bg-zinc-50 p-1"
        >
          <p id={`${scheduleModeId}-label`} className="sr-only">
            {t("scheduleModeLabel")}
          </p>
          <button
            type="button"
            role="radio"
            disabled={disabled}
            aria-checked={!isRecurrent}
            onClick={() => onIsRecurrentChange(false)}
            className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              !isRecurrent
                ? "bg-yellow-100 text-zinc-900 shadow-sm"
                : "text-neutral-600 hover:bg-white hover:text-zinc-900"
            }`}
          >
            {t("ponctualMode")}
          </button>
          <button
            type="button"
            role="radio"
            disabled={disabled}
            aria-checked={isRecurrent}
            onClick={() => onIsRecurrentChange(true)}
            className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              isRecurrent
                ? "bg-yellow-100 text-zinc-900 shadow-sm"
                : "text-neutral-600 hover:bg-white hover:text-zinc-900"
            }`}
          >
            {t("recurrentMode")}
          </button>
        </div>

        {!isRecurrent && ponctuelSchedule ? (
          <div className="space-y-3">
            {ponctuelSchedule.dates.length === 0 ? (
              <p className="text-sm text-neutral-500">{t("noDates")}</p>
            ) : (
              <ul className="space-y-2">
                {ponctuelSchedule.dates.map((date) => (
                  <li
                    key={date}
                    className="flex items-center justify-between gap-3 rounded-xl wireframe-border bg-zinc-50 px-3 py-2"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-whart-text">
                      <CalendarIcon />
                      {formatActivityDate(date)}
                    </span>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => handleRemoveDate(date)}
                      className="rounded-xl px-2 py-1 text-xs font-medium text-neutral-600 transition-colors hover:bg-white hover:text-whart-urgent disabled:opacity-50"
                      aria-label={t("removeDate", { date: formatActivityDate(date) })}
                    >
                      {t("removeDateAction")}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={newDate}
                disabled={disabled}
                onChange={(event) => setNewDate(event.target.value)}
                className={dateInputClassName}
                aria-label={t("addDateLabel")}
              />
              <button
                type="button"
                disabled={disabled || !newDate}
                onClick={handleAddDate}
                className="rounded-xl bg-yellow-100 px-3 py-2 text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:bg-amber-100 disabled:opacity-50"
              >
                {t("addDateAction")}
              </button>
            </div>
            <FieldError message={fieldErrors?.activity_schedule} />
          </div>
        ) : null}

        {isRecurrent && recurrentSchedule ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <FieldLabel htmlFor="event-activity-recurrence" required>
                {t("recurrenceLabel")}
              </FieldLabel>
              <select
                id="event-activity-recurrence"
                value={recurrentSchedule.reccurence}
                disabled={disabled}
                onChange={(event) =>
                  handleRecurrenceChange(
                    event.target.value as ActivityRecurrenceValue,
                  )
                }
                className={selectClassName}
              >
                {ACTIVITY_RECURRENCE_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <FieldError message={fieldErrors?.activity_schedule} />
            </div>

            <fieldset className="space-y-2">
              <FieldLabel as="legend" required>
                {t("weekdaysLabel")}
              </FieldLabel>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_WEEKDAY_VALUES.map((day) => {
                  const checked = recurrentSchedule.days.includes(day);

                  return (
                    <label
                      key={day}
                      className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                        checked
                          ? "border-zinc-900 bg-yellow-100 text-zinc-900"
                          : "border-black/10 bg-white text-neutral-600 hover:bg-zinc-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        name="activity_days"
                        value={String(day)}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => handleDayToggle(day)}
                        className="sr-only"
                      />
                      {t(`weekdays.${day}`)}
                    </label>
                  );
                })}
              </div>
              <FieldError message={fieldErrors?.activity_days} />
            </fieldset>
          </div>
        ) : null}
      </div>

      <input
        type="hidden"
        name="activity_schedule"
        value={JSON.stringify(schedule)}
      />
    </section>
  );
}

function CalendarIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="size-4 shrink-0 text-neutral-400"
    >
      <rect x="3.5" y="4.5" width="13" height="12" rx="1" />
      <path d="M6.5 3.5v2M13.5 3.5v2M3.5 8.5h13" />
    </svg>
  );
}
